import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Clock, User, Tag } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";
import LazyImage from "@/components/LazyImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchArticle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setArticle(response.data);
    } catch (error) {
      // handled silently
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#02040a]">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Artículo no encontrado</h1>
          <Link to="/blog" className="text-[#3b82f6] hover:underline">
            Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  // Convert markdown-like content to HTML
  const renderContent = (content) => {
    return content.split('\n').map((line, index) => {
      const lineKey = `line-${index}-${line.slice(0, 20).replace(/\s/g, '')}`;
      if (line.startsWith('## ')) {
        return <h2 key={lineKey} className="text-2xl font-bold text-white mt-8 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={lineKey} className="text-xl font-semibold text-white mt-6 mb-3">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('- **')) {
        const parts = line.replace('- **', '').split('**:');
        return (
          <li key={lineKey} className="text-[#94a3b8] mb-2 ml-4">
            <strong className="text-white">{parts[0]}</strong>: {parts[1]}
          </li>
        );
      }
      if (line.startsWith('- ')) {
        return <li key={lineKey} className="text-[#94a3b8] mb-2 ml-4">{line.replace('- ', '')}</li>;
      }
      if (line.match(/^\d+\. \*\*/)) {
        const parts = line.replace(/^\d+\. \*\*/, '').split('**:');
        return (
          <li key={lineKey} className="text-[#94a3b8] mb-2 ml-4 list-decimal">
            <strong className="text-white">{parts[0]}</strong>: {parts[1]}
          </li>
        );
      }
      if (line.trim() === '') {
        return <br key={lineKey} />;
      }
      return <p key={lineKey} className="text-[#94a3b8] leading-relaxed mb-4">{line}</p>;
    });
  };

  return (
    <div data-testid="blog-article-page" className="min-h-screen bg-[#02040a]">
      <SEO
        title={article?.title || "Blog"}
        description={article?.content?.substring(0, 160) || "Artículo del blog de Global Metafora APP"}
        path={`/blog/${slug}`}
        type="article"
      />
      <Navbar />
      
      <article className="pt-32 pb-20">
        <div className="section-container max-w-4xl">
          {/* Back link */}
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#3b82f6] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al Blog
          </Link>

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <span className="inline-block bg-[#3b82f6]/20 text-[#3b82f6] text-sm font-medium px-3 py-1 mb-4">
              {article.category}
            </span>
            <h1 
              data-testid="article-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            >
              {article.title}
            </h1>
            
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 text-[#94a3b8]">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{article.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(article.published_at).toLocaleDateString('es-ES', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{article.read_time} min de lectura</span>
              </div>
            </div>
          </motion.header>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="relative aspect-video mb-12 overflow-hidden"
          >
            <LazyImage
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#02040a]/50 to-transparent" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="article-content"
            className="prose prose-invert max-w-none"
          >
            {renderContent(article.content)}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16 glass-card p-8 text-center"
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              ¿Listo para Transformar tu Negocio?
            </h3>
            <p className="text-[#94a3b8] mb-6">
              Contáctanos hoy y descubre cómo Global Metafora puede ayudarte.
            </p>
            <Link
              to="/#contacto"
              data-testid="article-cta"
              className="btn-primary inline-block"
            >
              <span>Solicitar Consulta Gratis</span>
            </Link>
          </motion.div>
        </div>
      </article>

      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default BlogArticlePage;
